<script setup lang="ts">
  import { computed } from 'vue'
  import {
    FLOW_CARD_BUTTON_TEST_ID,
    FLOW_CARD_TITLE_TEST_ID,
    BUTTON_CLICK_EVENT_NAME,
    FLOW_CARD_TEST_ID,
  } from './FlowCardQC.constants'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { BackButtonQC } from '@/shared/components/BackButtonQC'
  import { useI18n } from 'vue-i18n'

  interface Props {
    title?: string
    withButton?: boolean
    buttonText?: string
    buttonWithArrowIcon?: boolean
    isLoading?: boolean
    withBackButton?: boolean
    disabledBackButton?: boolean
  }

  interface Emits {
    (e: typeof BUTTON_CLICK_EVENT_NAME): void
    (e: 'back'): void
  }

  interface Slots {
    header(): void
    default(): void
    button(): void
    after(): void
  }

  const props = withDefaults(defineProps<Props>(), {
    withButton: false,
    buttonText: undefined,
    title: '',
    buttonWithArrowIcon: true,
    isLoading: false,
    withBackButton: false,
    disabledBackButton: false,
  })
  const emit = defineEmits<Emits>()
  defineSlots<Slots>()

  const { locale, t } = useI18n()

  const buttonText = computed(() => props.buttonText ?? t('travel.common.continue.button'))

  const handleButtonClick = () => {
    if (props.disabledBackButton) return
    emit(BUTTON_CLICK_EVENT_NAME)
  }
</script>

<template>
  <WrapperQC class="flow-card ds-flow-card" :data-test="FLOW_CARD_TEST_ID">
    <WrapperQC
      v-if="title || $slots.header"
      class="flow-card__header"
      direction="column"
      gap="var(--spacing-3)"
    >
      <BackButtonQC v-if="withBackButton" :locale="locale" :disabled="disabledBackButton" @click="emit('back')" />
      
      <h1 v-if="title" class="flow-card__title" :data-test="FLOW_CARD_TITLE_TEST_ID">
        {{ title }}
      </h1>

      <slot name="header" />
    </WrapperQC>

    <slot />

    <WrapperQC
      v-if="withButton || $slots.button || $slots.after"
      class="flow-card__footer"
      direction="column"
      gap="var(--spacing-3)"
    >
      <slot v-if="withButton || $slots.button" name="button">
        <ButtonQC
          wide
          :data-test="FLOW_CARD_BUTTON_TEST_ID"
          :loading="isLoading"
          :icon-after-content="
            buttonWithArrowIcon ? `arrow-narrow-${locale === 'en' ? 'right' : 'left'}` : undefined
          "
          @click="handleButtonClick"
        >
          {{ buttonText }}
        </ButtonQC>
      </slot>
      <slot v-if="$slots.after" name="after" />
    </WrapperQC>
  </WrapperQC>
</template>

<style lang="scss" scoped>
  @import './FlowCardQC';
</style>
